import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useTenantCode } from '../shared/tenant/TenantProvider.js';
import { isAccountRoute } from '../features/more/moreRouteUtils.js';

interface OpenMoreOptions {
  readonly compact?: boolean;
}

export interface ShellSheetOpeners {
  readonly openSettings: () => void;
  readonly openProfile: () => void;
}

interface MoreShellContextValue {
  readonly isMoreOpen: boolean;
  readonly openMore: (options?: OpenMoreOptions) => void;
  readonly closeMore: () => void;
  readonly moreCompact: boolean;
  /**
   * HUX-01 — Settings/Profile leave root `/browse` first-row; open via More.
   * Header registers openers; More rows call these (drawer closes first).
   */
  readonly registerShellSheetOpeners: (openers: ShellSheetOpeners | null) => void;
  readonly openSettingsSheet: () => void;
  readonly openProfileSheet: () => void;
}

const MoreShellContext = createContext<MoreShellContextValue | null>(null);

export function MoreShellProvider({ children }: { children: ReactNode }): JSX.Element {
  const tenantCode = useTenantCode();
  const location = useLocation();
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [moreCompactOverride, setMoreCompactOverride] = useState<boolean | null>(null);
  const sheetOpenersRef = useRef<ShellSheetOpeners | null>(null);

  const routeCompact = isAccountRoute(location.pathname, tenantCode);
  const moreCompact = moreCompactOverride ?? routeCompact;

  const openMore = useCallback((options?: OpenMoreOptions): void => {
    setMoreCompactOverride(options?.compact ?? null);
    setIsMoreOpen(true);
  }, []);

  const closeMore = useCallback((): void => {
    setIsMoreOpen(false);
    setMoreCompactOverride(null);
  }, []);

  const registerShellSheetOpeners = useCallback((openers: ShellSheetOpeners | null): void => {
    sheetOpenersRef.current = openers;
  }, []);

  const openSettingsSheet = useCallback((): void => {
    setIsMoreOpen(false);
    setMoreCompactOverride(null);
    const open = sheetOpenersRef.current?.openSettings;
    if (open !== undefined) {
      void Promise.resolve().then(open);
    }
  }, []);

  const openProfileSheet = useCallback((): void => {
    setIsMoreOpen(false);
    setMoreCompactOverride(null);
    const open = sheetOpenersRef.current?.openProfile;
    if (open !== undefined) {
      void Promise.resolve().then(open);
    }
  }, []);

  const value = useMemo(
    (): MoreShellContextValue => ({
      isMoreOpen,
      openMore,
      closeMore,
      moreCompact,
      registerShellSheetOpeners,
      openSettingsSheet,
      openProfileSheet,
    }),
    [
      closeMore,
      isMoreOpen,
      moreCompact,
      openMore,
      openProfileSheet,
      openSettingsSheet,
      registerShellSheetOpeners,
    ],
  );

  return <MoreShellContext.Provider value={value}>{children}</MoreShellContext.Provider>;
}

export function useMoreShell(): MoreShellContextValue {
  const ctx = useContext(MoreShellContext);
  if (ctx === null) {
    throw new Error('useMoreShell must be used within MoreShellProvider');
  }
  return ctx;
}

/** Optional hook for BottomNav when rendered outside provider (legacy routes). */
export function useMoreShellOptional(): MoreShellContextValue | null {
  return useContext(MoreShellContext);
}
